import { useState } from "react";
import { Music, RotateCcw, Trash2, TableProperties, Sparkles, Clock, Bell, Zap, Users, Eye, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { EventActionsDropdown } from "@/components/event-actions-dropdown";
import { EventNotificationSettings } from "@/components/event-notification-settings";
import ParticipantStats from "@/components/participant-stats";
import { useLocation } from "wouter";
import type { JamNight, Performer } from "@shared/schema";

interface ManageTabProps {
  jamNight: JamNight | null | undefined;
  jamNightId: number;
  performers: Performer[];
  isEventCompleted: boolean;
  userRole: string | null | undefined;
  showConsiderationInNormalView: boolean;
  setShowConsiderationInNormalView: (value: boolean) => void;
  clearOffStagePerformersMutation: any;
  resetAllPerformersMutation: any;
  setShowTimeLimitModal: (value: boolean) => void;
  setShowRaffleModal: (value: boolean) => void;
}

export function ManageTab({
  jamNight,
  jamNightId,
  performers,
  isEventCompleted,
  userRole,
  showConsiderationInNormalView,
  setShowConsiderationInNormalView,
  clearOffStagePerformersMutation,
  resetAllPerformersMutation,
  setShowTimeLimitModal,
  setShowRaffleModal,
}: ManageTabProps) {
  const [, setLocation] = useLocation();
  const [showClearOffStageConfirm, setShowClearOffStageConfirm] = useState(false);
  const [showResetAllConfirm, setShowResetAllConfirm] = useState(false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-slate-800 drop-shadow-sm mb-2">{jamNight?.name}</h1>
        <p className="text-sm text-slate-600">Manage event settings and participant actions</p>
        
        {/* Event Administration Button - Moved here for better mobile accessibility */}
        {jamNight && (userRole === 'owner' || userRole === 'god') && (
          <div className="mt-4">
            <EventActionsDropdown
              jamNight={jamNight}
              canManageEvent={true}
              variant="button"
              className="mx-auto"
            />
          </div>
        )}
      </div>

      {/* Quick Actions - Show first on mobile, after stats on desktop */}
      <div className="block lg:hidden">
        {/* Event Controls Card - Mobile */}
        <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6 overflow-visible mb-6"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-5 w-5 text-slate-700" />
            <h3 className="text-lg font-semibold text-slate-800 drop-shadow-sm">Quick Actions</h3>
          </div>
          
          <div className="space-y-4">
            {/* Stage Management */}
            <div>
              <p className="text-xs text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Music className="h-3 w-3" />
                Stage Control
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  disabled={isEventCompleted || clearOffStagePerformersMutation.isPending || performers.filter(p => p.status === 'on_stage' || p.status === 'pending_removal').length === 0}
                  onClick={() => setShowClearOffStageConfirm(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All Off Stage
                </Button>
                
                {(userRole === 'owner' || userRole === 'god') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                    style={{
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                    onClick={() => setShowTimeLimitModal(true)}
                    disabled={isEventCompleted}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Set Performance Time Limit
                  </Button>
                )}
              </div>
            </div>
            
            {/* Participant Actions */}
            <div>
              <p className="text-xs text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Users className="h-3 w-3" />
                Participant Management
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  disabled={isEventCompleted || resetAllPerformersMutation.isPending || performers.length === 0}
                  onClick={() => setShowResetAllConfirm(true)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset All Participants
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  onClick={() => setLocation(`/event/${jamNightId}/participants`)}
                >
                  <TableProperties className="h-4 w-4 mr-2" />
                  View All Participant Data
                </Button>

                {jamNight?.raffleEnabled && (
                  <Button
                    variant="outline"
                    className="w-full justify-start bg-yellow-100/40 backdrop-blur-md border-yellow-200/50 text-yellow-700 hover:bg-yellow-100/60 hover:border-yellow-300/60 shadow-lg transition-all duration-300"
                    style={{
                      boxShadow: '0 4px 16px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                    }}
                    onClick={() => setShowRaffleModal(true)}
                    disabled={isEventCompleted || performers.filter(p => !p.removed && (p.raffleEntrances || 0) > 0).length === 0}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Run Raffle
                  </Button>
                )}
              </div>
            </div>
            
            {/* Event Feed */}
            <div>
              <p className="text-xs text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <ScrollText className="h-3 w-3" />
                Activity Tracking
              </p>
              <Button
                variant="outline"
                className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
                onClick={() => setLocation(`/events/${jamNightId}/feed`)}
              >
                <ScrollText className="h-4 w-4 mr-2" />
                View Event Feed
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <ParticipantStats performers={performers} />

      {/* Settings & Notifications - Mobile */}
      <div className="block lg:hidden space-y-6">
        {/* Push Notifications */}
        {jamNight && (
          <EventNotificationSettings eventId={jamNightId} eventName={jamNight.name} />
        )}
        
        {/* View Settings */}
        <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}>
          <div className="flex items-center gap-2 mb-4">
            <Eye className="h-5 w-5 text-slate-700" />
            <h3 className="text-lg font-semibold text-slate-800 drop-shadow-sm">Display Settings</h3>
          </div>
          
          <div className="space-y-3">
            <div className="p-4 bg-white/10 rounded-xl border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Consideration List</p>
                  <p className="text-xs text-slate-600 mt-1">Show consideration section in the live view tab</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowConsiderationInNormalView(!showConsiderationInNormalView)}
                  className={`${
                    showConsiderationInNormalView 
                      ? 'bg-green-100/40 border-green-300/50 hover:bg-green-100/60' 
                      : 'bg-white/40 border-white/30 hover:bg-white/50'
                  } backdrop-blur-md shadow-sm transition-all duration-300`}
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  {showConsiderationInNormalView ? 'Visible' : 'Hidden'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Management Grid - Desktop only */}
      <div className="hidden lg:grid gap-6 lg:grid-cols-2 relative">
        
        {/* Event Controls Card */}
        <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6 overflow-visible"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-5 w-5 text-slate-700" />
            <h3 className="text-lg font-semibold text-slate-800 drop-shadow-sm">Quick Actions</h3>
          </div>
          
          <div className="space-y-4">
            {/* Stage Management */}
            <div>
              <p className="text-xs text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Music className="h-3 w-3" />
                Stage Control
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  disabled={isEventCompleted || clearOffStagePerformersMutation.isPending || performers.filter(p => p.status === 'on_stage' || p.status === 'pending_removal').length === 0}
                  onClick={() => setShowClearOffStageConfirm(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All Off Stage
                </Button>
                
                {(userRole === 'owner' || userRole === 'god') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                    style={{
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                    onClick={() => setShowTimeLimitModal(true)}
                    disabled={isEventCompleted}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Set Performance Time Limit
                  </Button>
                )}
              </div>
            </div>
            
            {/* Participant Actions */}
            <div>
              <p className="text-xs text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Users className="h-3 w-3" />
                Participant Management
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  disabled={isEventCompleted || resetAllPerformersMutation.isPending || performers.length === 0}
                  onClick={() => setShowResetAllConfirm(true)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset All Participants
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  onClick={() => setLocation(`/event/${jamNightId}/participants`)}
                >
                  <TableProperties className="h-4 w-4 mr-2" />
                  View All Participant Data
                </Button>

                {jamNight?.raffleEnabled && (
                  <Button
                    variant="outline"
                    className="w-full justify-start bg-yellow-100/40 backdrop-blur-md border-yellow-200/50 text-yellow-700 hover:bg-yellow-100/60 hover:border-yellow-300/60 shadow-lg transition-all duration-300"
                    style={{
                      boxShadow: '0 4px 16px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                    }}
                    onClick={() => setShowRaffleModal(true)}
                    disabled={isEventCompleted || performers.filter(p => !p.removed && (p.raffleEntrances || 0) > 0).length === 0}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Run Raffle
                  </Button>
                )}
              </div>
            </div>
            
            {/* Event Feed */}
            <div>
              <p className="text-xs text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <ScrollText className="h-3 w-3" />
                Activity Tracking
              </p>
              <Button
                variant="outline"
                className="w-full justify-start bg-white/30 backdrop-blur-md border-white/30 hover:bg-white/40 shadow-sm transition-all duration-300"
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
                onClick={() => setLocation(`/events/${jamNightId}/feed`)}
              >
                <ScrollText className="h-4 w-4 mr-2" />
                View Event Feed
              </Button>
            </div>
            
          </div>
        </div>

        {/* Settings & Notifications Column */}
        <div className="space-y-6">
          {/* Push Notifications */}
          {jamNight && (
            <EventNotificationSettings eventId={jamNightId} eventName={jamNight.name} />
          )}
          
          {/* View Settings */}
          <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}>
            <div className="flex items-center gap-2 mb-4">
              <Eye className="h-5 w-5 text-slate-700" />
              <h3 className="text-lg font-semibold text-slate-800 drop-shadow-sm">Display Settings</h3>
            </div>
            
            <div className="space-y-3">
              <div className="p-4 bg-white/10 rounded-xl border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Consideration List</p>
                    <p className="text-xs text-slate-600 mt-1">Show consideration section in the live view tab</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConsiderationInNormalView(!showConsiderationInNormalView)}
                    className={`${
                      showConsiderationInNormalView 
                        ? 'bg-green-100/40 border-green-300/50 hover:bg-green-100/60' 
                        : 'bg-white/40 border-white/30 hover:bg-white/50'
                    } backdrop-blur-md shadow-sm transition-all duration-300`}
                    style={{
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {showConsiderationInNormalView ? 'Visible' : 'Hidden'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={showClearOffStageConfirm}
        onOpenChange={setShowClearOffStageConfirm}
        title="Clear All Off Stage?"
        description="This will take all performers currently on stage and move them to off stage. Their performance time will be calculated and saved. This action cannot be undone."
        onConfirm={() => clearOffStagePerformersMutation.mutate()}
        confirmText="Yes, Clear Stage"
        isConfirming={clearOffStagePerformersMutation.isPending}
      />
      
      <ConfirmationDialog
        isOpen={showResetAllConfirm}
        onOpenChange={setShowResetAllConfirm}
        title="Reset All Participants?"
        description={`This will reset all participants back to "not played" status and clear their stage time. Anyone currently on stage or selected for stage will be reset. All replacement information will be cleared. This action cannot be undone.`}
        onConfirm={() => resetAllPerformersMutation.mutate()}
        confirmText="Yes, Reset All"
        isConfirming={resetAllPerformersMutation.isPending}
      />
    </div>
  );
}